/**
 * BasicInfoTab — Resident basic info display.
 *
 * Shows:
 *   - Profile photo with Add/Update button
 *   - Two-column info grid: name, nickname, phone, email, notes, area,
 *     area type, move-in date, groups, EHR ID, care level + review badge
 *   - Moved-out banner when resident is inactive
 */

import { Link } from 'react-router-dom';
import { convertIsoUTCToLocal } from '../../../../../libs.jsx';
import ResidentEpisodePanel from '../../../../../components/resident-trends/ResidentEpisodePanel.jsx';

const BasicInfoTab = ({
  firstName, lastName, nickName, phone, email, notes,
  areaName, areaType, areaId,
  locatingActive = false, locating = null,
  imageUrl, stockPhoto, isActive,
  moveInDate, moveOutDate,
  resGroups, ehrId, ehrData, basicData,
  isReview, reviewIndex, tagColor, flagData,
  tzOffset, loading, editRights,
  onEdit, onPhotoUpload,
  campusId, residentId, showTrends,
  trendEpisodes, trendMutes, onTrendsChanged,
  emergencyContacts, onAddContact, onEditContact, onDeleteContact,
}) => {
  const careLevel = ehrData?.CareLevel || basicData?.ehr_level_of_care || '';

  return (
    <>
      {/* Moved-out banner */}
      {!isActive ? (
        <div className="rpp-moved-out-banner">
          <span>Moved Out</span>
          {moveOutDate ? (
            <span style={{ fontWeight: 'var(--font-weight-normal)' }}>
              {convertIsoUTCToLocal(moveOutDate, tzOffset)}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Active Resident Trends concerns (campus feature flag; waits for the
          profile to load). Closed episodes live on the Trend History tab. */}
      {showTrends && !loading && campusId && residentId ? (
        <ResidentEpisodePanel
          campusId={campusId}
          episodes={trendEpisodes}
          mutes={trendMutes}
          onChanged={onTrendsChanged}
        />
      ) : null}

      {/* Basic Info section */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Resident Details</h3>
          <div className="pp-section__actions">
            {editRights ? (
              <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={onEdit}>
                &#x270E; Edit
              </button>
            ) : null}
          </div>
        </div>
        <div className="pp-section__body">
          <div className="rpp-basic-layout">
            {/* Photo column */}
            <div className="rpp-photo-container">
              {/* The modifier matters: dark mode inverts the placeholder so it
                  stops being a bright grey block on a dark card, and inverting a
                  real resident photo would be a disaster. */}
              <img
                className={`rpp-photo${imageUrl ? '' : ' rpp-photo--placeholder'}`}
                src={imageUrl || stockPhoto}
                alt="Profile Photo"
              />
              {editRights ? (
                <button
                  className="rpp-photo-btn"
                  onClick={onPhotoUpload}
                >
                  {imageUrl ? 'Update Image' : 'Add Image'}
                </button>
              ) : null}
            </div>

            {/* Info grid */}
            <div className="rpp-info-grid">
              {/* Col 1 fields */}
              <div className="pp-field">
                <span className="pp-field__label">Name</span>
                <span className="pp-field__value">
                  {firstName || lastName ? `${firstName} ${lastName}`.trim() : '\u2014'}
                </span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Area</span>
                <span className="pp-field__value">
                  {areaName ? (
                    <Link to={`/community/areas/${areaId}`}>{areaName}</Link>
                  ) : '\u2014'}
                </span>
              </div>

              {/* Real-time Locating (RTLS) \u2014 only on campuses with an active
                  vendor 14. Value links to the located area's profile; the
                  reading's campus-local timestamp shows on hover. */}
              {locatingActive ? (
                <div className="pp-field">
                  <span className="pp-field__label">Real-time Locating</span>
                  <span
                    className="pp-field__value"
                    title={locating?.timestamp ? `Last update: ${locating.timestamp}` : undefined}
                  >
                    {locating?.area_name ? (
                      locating.area_id ? (
                        <Link to={`/community/areas/${locating.area_id}`}>{locating.area_name}</Link>
                      ) : locating.area_name
                    ) : '\u2014'}
                  </span>
                </div>
              ) : null}

              <div className="pp-field">
                <span className="pp-field__label">Nickname</span>
                <span className="pp-field__value">{nickName || '\u2014'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Area Type</span>
                <span className="pp-field__value">{areaType || '\u2014'}</span>
              </div>

              <div className="pp-field">
                <span className="pp-field__label">Phone</span>
                <span className="pp-field__value">{phone || '\u2014'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Moved-in</span>
                <span className="pp-field__value">
                  {moveInDate && !['0000-00-00 00:00:00'].includes(moveInDate)
                    ? convertIsoUTCToLocal(moveInDate, tzOffset)
                    : 'Not Recorded'}
                </span>
              </div>

              <div className="pp-field">
                <span className="pp-field__label">Email</span>
                <span className="pp-field__value">{email || '\u2014'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">EHR ID</span>
                <span className="pp-field__value">{ehrId || 'N/A'}</span>
              </div>

              <div className="pp-field" style={{ gridColumn: '1 / -1' }}>
                <span className="pp-field__label">Notes</span>
                <span className="pp-field__value" style={{ whiteSpace: 'pre-wrap' }}>
                  {notes || '\u2014'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Care Level + Review Flag */}
      {careLevel ? (
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Care Level</h3>
          </div>
          <div className="pp-section__body">
            <div className="pp-field-grid">
              <div className="pp-field">
                <span className="pp-field__label">Level of Care</span>
                <span className="pp-field__value">
                  {careLevel}
                  {isReview ? (
                    <span
                      className="rpp-review-badge"
                      style={{ background: tagColor, marginLeft: 'var(--space-2)' }}
                      title="Call volume suggests this resident could be at an inappropriate level of care."
                    >
                      Review
                    </span>
                  ) : null}
                </span>
              </div>
              {reviewIndex > 0 ? (
                <div className="pp-field">
                  <span className="pp-field__label">Review Index</span>
                  <span className="pp-field__value">{reviewIndex.toFixed(2)}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* Groups */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">
            Groups
            <span className={"pp-section__count"}>({resGroups?.length})</span>
          </h3>
        </div>
        <div className="pp-section__body">
          {resGroups?.length > 0 ? (
            <div className="pp-groups-list">
              {resGroups.map((g, idx) => (
                <span key={g.resident_group_id || idx} className="pp-group-tag">
                  {g.resident_group_name || `Group ${g.resident_group_id}`}
                </span>
              ))}
            </div>
          ) : (
            <span className="dp-cell-text dp-cell-text--muted">No groups assigned</span>
          )}
        </div>
      </div>

      {/* Emergency Contacts */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">
            Emergency Contacts
            <span className="pp-section__count">({emergencyContacts?.length || 0})</span>
          </h3>
          <div className="pp-section__actions">
            {editRights ? (
              <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={onAddContact}>
                + Add Contact
              </button>
            ) : null}
          </div>
        </div>
        <div className="pp-section__body">
          {emergencyContacts?.length > 0 ? (
            <div className="rpp-contacts-list">
              {emergencyContacts.map((c) => (
                <div
                  key={c.monitoring_contact_id}
                  className="rpp-contact-card"
                >
                  <div className="rpp-contact-card__header">
                    <span className="rpp-contact-card__name">
                      {[c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ') || 'Unnamed'}
                    </span>
                    {c.relationship ? (
                      <span className="rpp-contact-card__relationship">{c.relationship}</span>
                    ) : null}
                  </div>
                  <div className="rpp-contact-card__details">
                    {c.contact_phone ? (
                      <span className="rpp-contact-card__item">
                        <svg className="rpp-contact-card__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                        </svg>
                        <a href={`tel:${c.contact_phone}`} className="rpp-contact-card__link">{c.contact_phone}</a>
                      </span>
                    ) : null}
                    {c.contact_email ? (
                      <span className="rpp-contact-card__item">
                        <svg className="rpp-contact-card__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                        <a href={`mailto:${c.contact_email}`} className="rpp-contact-card__link">{c.contact_email}</a>
                      </span>
                    ) : null}
                  </div>
                  {editRights ? (
                    <div className="rpp-contact-card__actions">
                      <button
                        type="button"
                        className="rpp-contact-card__action-btn"
                        onClick={() => onEditContact(c)}
                        title="Edit contact"
                      >
                        <i className="fe fe-edit" />
                      </button>
                      <button
                        type="button"
                        className="rpp-contact-card__action-btn rpp-contact-card__action-btn--danger"
                        onClick={() => onDeleteContact(c)}
                        title="Remove contact"
                      >
                        <i className="fe fe-trash-2" />
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <span className="dp-cell-text dp-cell-text--muted">No emergency contacts</span>
          )}
        </div>
      </div>
    </>
  );
};

export default BasicInfoTab;
